import { Fragment } from 'react';
import { segmentAuthoredBody } from '../../lib/templates/authoredBodyRedaction';
import { Card } from '../ui/Card';
import { Skeleton } from '../admin/Skeleton';
import { useSentPreview } from '../../hooks/useSentPreview';

/**
 * PII-safe "Sent" message preview (PRO-0620 follow-up). Renders the AUTHORED,
 * send-level stored body (merge tokens STILL UNRESOLVED) with each `{{token}}`
 * replaced by a NEUTRAL placeholder chip (friendly label). It NEVER resolves a
 * token to a recipient value and NEVER reads per-recipient outbound content
 * (SEC-8) — deterministic placeholder redaction only, not free-text NER (SEC-3).
 * Behind the same `UCP_COMMS_HOME_ACTIVITY_ENABLED` gate as the rest of the
 * drawer (the server 503s when off).
 */
export interface SentMessagePreviewProps {
  readonly sendId: string;
}

/** Render an authored body as readable text + placeholder chips (line breaks kept). */
function renderAuthoredBody(body: string): JSX.Element {
  const segments = segmentAuthoredBody(body);
  return (
    <>
      {segments.map((segment, index) =>
        segment.kind === 'text' ? (
          // Preserve authored line breaks; `whitespace-pre-wrap` on the container
          // keeps newlines readable without introducing raw HTML.
          <Fragment key={index}>{segment.text}</Fragment>
        ) : (
          <span
            key={index}
            role="img"
            aria-label={`placeholder: ${segment.label} — filled in for each recipient when sent`}
            className="mx-0.5 inline-block rounded-sm bg-chip-bg px-1.5 py-0 align-baseline font-mono text-sm text-chip-foreground"
          >
            [{segment.label}]
          </span>
        ),
      )}
    </>
  );
}

export function SentMessagePreview({ sendId }: SentMessagePreviewProps): JSX.Element {
  const { status, preview, error } = useSentPreview(sendId);

  return (
    <section aria-labelledby="sent-preview-heading" className="mt-6">
      <h4 id="sent-preview-heading" className="mb-2 mt-0 font-semibold text-text-primary">
        Message preview
      </h4>

      {status === 'loading' ? (
        <Skeleton rows={4} label="Loading message preview" />
      ) : status === 'error' ? (
        <Card className="p-4">
          <p role="alert" className="m-0 text-base text-text-secondary">
            {error ?? 'Could not load the message preview just now.'}
          </p>
        </Card>
      ) : preview && preview.available && preview.body !== null ? (
        <Card className="p-4">
          <p className="m-0 mb-3 text-xs text-text-secondary">
            Merge fields are shown as neutral placeholders — the values are filled in for each
            recipient when the message is sent, and are never stored here.
          </p>
          {preview.subject !== null ? (
            <p className="m-0 mb-2 text-sm">
              <span className="font-semibold text-text-secondary">Subject: </span>
              <span className="text-text-primary">{renderAuthoredBody(preview.subject)}</span>
            </p>
          ) : null}
          <div className="m-0 whitespace-pre-wrap break-words text-base text-text-primary">
            {renderAuthoredBody(preview.body)}
          </div>
        </Card>
      ) : (
        <Card className="p-4">
          <p className="m-0 text-base text-text-secondary">Preview not available for this send.</p>
        </Card>
      )}
    </section>
  );
}
